import React, {useState, useEffect} from 'react';
import AppBar from "../components/app_bar/AppBar";
import Drawer from "../components/Drawer";
import { 
    Typography, CssBaseline, Box, Toolbar, Grid, Card, CardContent, 
    useMediaQuery, Container, Paper, Stack, Chip, Button, Divider,
    Tabs, Tab, Fade, Zoom
} from '@mui/material';
import { TabPanel, TabContext, TabList  } from '@mui/lab';
import { useTheme } from '@mui/material/styles';
import { useParams } from "react-router-dom";
import { 
    Book as BookIcon,
    School as SchoolIcon,
    Info as InfoIcon,
    Category as CategoryIcon
} from '@mui/icons-material';

import * as CONSTANTS from "../constants"
import { explainedTerms } from '../mappings/explainedTerms';

const TermsExplainedCard = ({explainedTerm, lang, categoryDict, index}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    return (
        <Zoom in={true} style={{ transitionDelay: `${index * 100}ms` }}>
            <Card 
                elevation={2}
                sx={{ 
                    height: '100%',
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: theme.shadows[8],
                    }
                }}
            >
                <CardContent sx={{ p: 3 }}>
                    <Box>
                        {/* Title and Category (Chip) */}
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                            <Typography 
                                variant="h6" 
                                fontWeight="bold"
                                sx={{ 
                                    flex: 1,
                                    mr: 2,
                                    fontSize: isMobile ? '1.1rem' : '1.25rem',
                                    lineHeight: 1.3
                                }}
                            >
                                {explainedTerm.title[lang]}
                            </Typography>
                            <Chip 
                                label={categoryDict[explainedTerm.category]}
                                variant="filled"
                                size='small'
                                color="primary"
                                sx={{
                                    borderRadius: '1rem',
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    minWidth: 'fit-content'
                                }}
                            />
                        </Box>
                        
                        <Divider sx={{ mb: 2 }} />
                        
                        {/* Description */}
                        <Typography 
                            variant="body2" 
                            color="text.secondary"
                            sx={{ 
                                lineHeight: 1.6,
                                fontSize: isMobile ? '0.875rem' : '1rem'
                            }}
                        >
                            {explainedTerm.description[lang]}
                        </Typography>
                    </Box>
                </CardContent>
            </Card>
        </Zoom>
    )
}

const filter_explained_terms_by_category = (category) => {
    if (category == 0) {
        return explainedTerms;
    }
    return explainedTerms.filter((explainedTerm) => explainedTerm.category == category);
}

const TermsExplained = () => {

    document.title = CONSTANTS.TITLE

    let {lang} = useParams();

    const [stat, setStat] = useState(null);
    const [drawerOpen, setDrawerOpen] = useState(false);

    const handleDrawerOpen = (open) => {
        if (open === undefined) {
            setDrawerOpen(!drawerOpen)
        } else {
            setDrawerOpen(open);
        }
    }
    const [tabValue, setTabValue] = useState('0');

    const handleChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));

    const explainedTerms_all = filter_explained_terms_by_category(0);
    const explainedTerms_wazaren = filter_explained_terms_by_category(1);
    const explainedTerms_wotageishi = filter_explained_terms_by_category(2);
    const explainedTerms_organization = filter_explained_terms_by_category(3);
    const explainedTerms_movement = filter_explained_terms_by_category(4);
    const explainedTerms_video = filter_explained_terms_by_category(5);

    const categoryDict = {
        0: "N/A",
        1: CONSTANTS.DRAWER.WAZAREN[lang],
        2: CONSTANTS.DRAWER.WOTAGEISHI[lang],
        3: CONSTANTS.DRAWER.ORGANIZATION[lang],
        4: CONSTANTS.DRAWER.MOVEMENT[lang],
        5: CONSTANTS.WAZAREN_DETAILS.VIDEO[lang]
    };

    // Hero Section Component
    const HeroSection = () => (
        <Box
            sx={{
                position: 'relative',
                height: isMobile ? '40vh' : '50vh',
                minHeight: isMobile ? '300px' : '400px',
                width: "100%",
                background: `linear-gradient(135deg, ${theme.palette.primary.main}15, ${theme.palette.secondary.main}15)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 2,
                overflow: 'hidden',
                mb: 4,
                border: `1px solid ${theme.palette.primary.light}30`
            }}
        >
            <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
                <Box sx={{ textAlign: 'center' }}>
                    <Stack direction="row" justifyContent="center" alignItems="center" mb={2}>
                        <BookIcon 
                            sx={{ 
                                fontSize: 48, 
                                color: 'primary.main',
                                mr: 2
                            }} 
                        />
                        <Typography
                            variant={isMobile ? "h4" : "h3"}
                            sx={{ 
                                fontWeight: 'bold',
                                color: 'text.primary',
                                lineHeight: 1.2
                            }}
                        >
                            {CONSTANTS.DRAWER.TERMS_EXPLAINED[lang]}
                        </Typography>
                    </Stack>
                    <Typography
                        variant={isMobile ? "body1" : "h6"}
                        sx={{ 
                            color: 'text.secondary',
                            maxWidth: '600px',
                            mx: 'auto',
                            lineHeight: 1.6
                        }}
                    >
                        {CONSTANTS.TERMS_EXPLAINED_PAGE.HERO_SUBTITLE[lang]}
                    </Typography>
                </Box>
            </Container>
        </Box>
    );

    // Tab Content Component
    const TabContent = ({ terms, categoryDict }) => (
        <Box pb={3}>
            <Grid container spacing={3}>
                {terms.map((explainedTerm, index) => (
                    <Grid item xs={12} sm={6} lg={4} key={index}>
                        <TermsExplainedCard 
                            explainedTerm={explainedTerm} 
                            lang={lang} 
                            categoryDict={categoryDict}
                            index={index}
                        />
                    </Grid>
                ))}
            </Grid>
            {terms.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                    <InfoIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary">
                        {CONSTANTS.TERMS_EXPLAINED_PAGE.NO_TERMS_FOUND[lang]}
                    </Typography>
                </Box>
            )}
        </Box>
    );

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', overflowX: 'hidden' }}>
            <CssBaseline />
            <AppBar lang={lang} handleDrawerOpen={handleDrawerOpen}/>
            <Box sx={{ display: 'flex', width: '100%' }}>
                <Drawer lang={lang} drawerOpen={drawerOpen} setDrawerOpen={setDrawerOpen} />
                <Box component="main" sx={{ flexGrow: 1, width: '100%' }}>
                    <Toolbar />
                    <Container 
                        maxWidth={isMobile ? 'sm' : 'lg'} 
                        sx={{ py: isMobile ? 2 : 4, width: '100%', maxWidth: '100vw!important', px: isMobile ? 0.5 : 2 }}
                    >
                        {/* Hero Section */}
                        <HeroSection />

                        {/* Tabs Section */}
                        <Paper 
                            elevation={2}
                            sx={{ 
                                borderRadius: 2,
                                overflow: 'hidden',
                                width: '100%',
                                maxWidth: '100vw',
                                boxSizing: 'border-box'
                            }}
                        >
                            <TabContext value={tabValue}>
                                <Box sx={{ 
                                    borderBottom: 1, 
                                    borderColor: 'divider',
                                    bgcolor: 'background.paper',
                                    width: '100%',
                                    overflowX: 'auto'
                                }}>
                                    <TabList 
                                        onChange={handleChange} 
                                        variant="scrollable"
                                        scrollButtons="auto"
                                        sx={{
                                            '& .MuiTab-root': {
                                                minHeight: 64,
                                                fontSize: isMobile ? '0.875rem' : '1rem',
                                                fontWeight: 600,
                                                textTransform: 'none',
                                                maxWidth: '100vw',
                                            }
                                        }}
                                    >
                                        <Tab 
                                            label={
                                                <Stack direction="row" alignItems="center" spacing={1}>
                                                    <CategoryIcon fontSize="small" />
                                                    <span>{CONSTANTS.TERMS_EXPLAINED.ALL[lang]}</span>
                                                </Stack>
                                            } 
                                            value="0" 
                                        />
                                        <Tab 
                                            label={CONSTANTS.DRAWER.WAZAREN[lang]} 
                                            value="1" 
                                        />
                                        <Tab 
                                            label={CONSTANTS.DRAWER.WOTAGEISHI[lang]} 
                                            value="2" 
                                        />
                                        <Tab 
                                            label={CONSTANTS.DRAWER.ORGANIZATION[lang]} 
                                            value="3" 
                                        />
                                        <Tab 
                                            label={CONSTANTS.DRAWER.MOVEMENT[lang]} 
                                            value="4" 
                                        />
                                        <Tab 
                                            label={CONSTANTS.WAZAREN_DETAILS.VIDEO[lang]} 
                                            value="5" 
                                        />
                                    </TabList>
                                </Box>
                                
                                <Box sx={{ p: isMobile ? 2 : 3, width: '100%' }}>
                                    <TabPanel value="0" sx={{ p: 0 }}>
                                        <TabContent terms={explainedTerms_all} categoryDict={categoryDict} />
                                    </TabPanel>
                                    <TabPanel value="1" sx={{ p: 0 }}>
                                        <TabContent terms={explainedTerms_wazaren} categoryDict={categoryDict} />
                                    </TabPanel>
                                    <TabPanel value="2" sx={{ p: 0 }}>
                                        <TabContent terms={explainedTerms_wotageishi} categoryDict={categoryDict} />
                                    </TabPanel>
                                    <TabPanel value="3" sx={{ p: 0 }}>
                                        <TabContent terms={explainedTerms_organization} categoryDict={categoryDict} />
                                    </TabPanel>
                                    <TabPanel value="4" sx={{ p: 0 }}>
                                        <TabContent terms={explainedTerms_movement} categoryDict={categoryDict} />
                                    </TabPanel>
                                    <TabPanel value="5" sx={{ p: 0 }}>
                                        <TabContent terms={explainedTerms_video} categoryDict={categoryDict} />
                                    </TabPanel>
                                </Box>
                            </TabContext>
                        </Paper>
                    </Container>
                </Box>
            </Box>
        </Box>
    );
};
 
export default TermsExplained;